// Sesión del usuario: guarda token+usuario en localStorage y expone
// login/logout/updateUser junto con banderas de rol (isAdmin, isEmpresa...)
// para que el resto de la app no tenga que repetir comparaciones de role.
import { createContext, useContext, useState, useEffect } from "react";
import { api } from "../api";

const AuthContext = createContext();
const SESSION_KEY = "sst_session";

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);

  // Al montar, restaura la sesión guardada y confirma contra el backend
  // que el token siga siendo válido (por si expiró o la sesión fue cerrada
  // desde otro dispositivo).
  useEffect(() => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        const { token: t, user: u } = JSON.parse(raw);
        setToken(t);
        setUser(u);
        // Verificar que la sesión siga válida (api.me() ya usa REACT_APP_API_URL)
        api.me().then(res => {
          if (res?.error) logout();
        }).catch(() => logout());
      } catch {
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = (token, user) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ token, user }));
    setToken(token);
    setUser(user);
  };

  // Actualiza campos del usuario en memoria y en localStorage (ej. tras
  // cambiar su foto de perfil), sin necesidad de volver a loguearse.
  const updateUser = (patch) => {
    setUser(prev => {
      const next = { ...prev, ...patch };
      localStorage.setItem(SESSION_KEY, JSON.stringify({ token, user: next }));
      return next;
    });
  };

  // Cierra sesión: avisa al backend para invalidar el token server-side
  // (best-effort, no bloquea el logout local si falla) y limpia todo localmente.
  const logout = () => {
    if (localStorage.getItem(SESSION_KEY)) {
      api.logout().catch(() => {});
    }
    localStorage.removeItem(SESSION_KEY);
    setToken(null);
    setUser(null);
  };

  // Atajos de rol usados en toda la app para mostrar/ocultar acciones.
  const isAdmin    = user?.role === "admin";
  const isEmpresa  = user?.role === "empresa";
  const isAuxiliar = user?.role === "auxiliar";
  const canDelete  = isAdmin;
  const canMoveDocuments = isAdmin || (isAuxiliar && !!user?.can_move_documents);
  const canManageCompanies = isAdmin;
  const canManageUsers     = isAdmin;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, isAdmin, isEmpresa, isAuxiliar, canDelete, canMoveDocuments, canManageCompanies, canManageUsers }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
